import { API_BASE_URL } from './api';

interface RecaptchaApi {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: RecaptchaApi;
  }
}

const RECAPTCHA_SCRIPT_ID = 'stylere-recaptcha-v3-script';

type RecaptchaConfig = {
  siteKey: string | null;
  bypass: boolean;
};

let siteKeyPromise: Promise<RecaptchaConfig> | null = null;
let scriptPromise: Promise<void> | null = null;
let loadedSiteKey: string | null = null;

async function fetchRecaptchaConfig(): Promise<RecaptchaConfig> {
  const viteSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim();
  if (viteSiteKey) {
    return { siteKey: viteSiteKey, bypass: false };
  }

  if (!siteKeyPromise) {
    siteKeyPromise = (async () => {
      const response = await fetch(`${API_BASE_URL}/api/auth/captcha-site-key`);
      if (!response.ok) {
        throw new Error('Signup is temporarily unavailable. CAPTCHA is not configured yet.');
      }

      const data = (await response.json()) as { siteKey?: string | null; bypass?: boolean };
      const siteKey = data.siteKey?.trim() || null;
      const bypass = Boolean(data.bypass);

      if (!siteKey && !bypass) {
        throw new Error('Signup is temporarily unavailable. CAPTCHA is not configured yet.');
      }

      return { siteKey, bypass };
    })().catch((error) => {
      siteKeyPromise = null;
      throw error;
    });
  }

  return siteKeyPromise;
}

async function loadRecaptchaScript(siteKey: string): Promise<RecaptchaApi> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('reCAPTCHA can only run in the browser.');
  }

  if (window.grecaptcha?.execute && loadedSiteKey === siteKey) {
    return window.grecaptcha;
  }

  if (!scriptPromise || loadedSiteKey !== siteKey) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        loadedSiteKey = siteKey;
        resolve();
      };
      script.onerror = () => {
        scriptPromise = null;
        loadedSiteKey = null;
        reject(new Error('Failed to load reCAPTCHA. Please try again.'));
      };

      document.head.appendChild(script);
    });
  }

  await scriptPromise;

  if (!window.grecaptcha?.ready || !window.grecaptcha?.execute) {
    scriptPromise = null;
    loadedSiteKey = null;
    throw new Error('reCAPTCHA failed to initialize. Please refresh and try again.');
  }

  return window.grecaptcha;
}

export async function getRecaptchaToken(action = 'register'): Promise<string | null> {
  const { siteKey, bypass } = await fetchRecaptchaConfig();
  if (bypass || !siteKey) {
    return null;
  }

  const recaptcha = await loadRecaptchaScript(siteKey);

  return new Promise<string>((resolve, reject) => {
    recaptcha.ready(() => {
      recaptcha
        .execute(siteKey, { action })
        .then((token) => {
          if (!token) {
            reject(new Error('reCAPTCHA returned an empty token. Please try again.'));
            return;
          }

          resolve(token);
        })
        .catch((error: unknown) => {
          reject(error instanceof Error ? error : new Error('Failed to verify reCAPTCHA. Please try again.'));
        });
    });
  });
}
